// src/pages/HomePage.tsx

/**
 * Enterprise dashboard landing page for the Quantik Mind frontend.
 *
 * This page is responsible for:
 * - loading the list of available projects
 * - retrieving recent execution runs for each project
 * - computing dashboard summary metrics
 * - allowing the user to select the active project context
 *
 * The dashboard acts as the main operational overview for recent Quantum
 * decision activity across projects.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Stack,
  Grid,
  Chip,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InsightsIcon from '@mui/icons-material/Insights';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { fetchAllRuns } from '../api/runs';
import { fetchProjects } from '../api/projects';
import { AuthError } from '../utils/apiClient';
import ActionButton from '../components/common/ActionButton';
import { formatPercentage } from '../utils/percentage';

/**
 * Minimal project shape required by the dashboard.
 */
interface ProjectSummary {
  /** Unique project identifier. */
  id: string;

  /** Human-readable project name. */
  name: string;
}

/**
 * Minimal run shape required by the dashboard.
 */
interface RunSummary {
  /** Unique run identifier. */
  run_id?: string;

  /** Project to which the run belongs. */
  project_id: string;

  /** Run creation timestamp. */
  created_at: string;

  /** Number of selected experiments executed in the run. */
  selected_experiments: number;

  /** Total number of experiments available for the run. */
  total_experiments: number;

  /** Optional run classification label. */
  run_type?: string;

  /** Optional run source label (e.g. 'seed', 'demo', 'ci'). */
  run_source?: string;

  /** Optional precomputed reduction percentage returned by the backend. */
  reduction_percentage?: number | null;

  /** Optional risk coverage field returned by some API payload variants. */
  risk_coverage?: number;

  /** Optional camelCase risk coverage field returned by some API payload variants. */
  riskCoverage?: number;

  /** Count-based top-quartile risk coverage returned by the list endpoint batch SQL. */
  top_risk_coverage?: number | null;

  /** High-risk signal coverage returned by the functional list endpoint. */
  high_risk_coverage?: number | null;
}

/**
 * Render the main dashboard page.
 *
 * @returns The enterprise dashboard page.
 */
const HomePage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [runsByProject, setRunsByProject] = useState<Record<string, RunSummary[]>>({});
  const [error, setError] = useState<string | null>(null);

  const { currentProject, setCurrentProject } = useProject();

  useEffect(() => {
    /**
     * Load the list of projects available to the current user.
     */
    const loadProjects = async () => {
      try {
        const data = await fetchProjects();
        setProjects(Array.isArray(data) ? data : []);
      } catch (err) {
        /**
         * Authentication failures are already handled centrally by the API layer.
         */
        if (err instanceof AuthError) {
          return;
        }

        console.error('Failed to load projects:', err);
        setError('Failed to load projects');
      }
    };

    loadProjects();
  }, []);

  useEffect(() => {
    /**
     * Load recent runs for each project and keep only executions from the last
     * 30 days for dashboard aggregation.
     */
    const loadRuns = async () => {
      if (projects.length === 0) {
        return;
      }

      try {
        const runsAccumulator: Record<string, RunSummary[]> = {};
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        for (const project of projects) {
          /**
           * Retrieve a large window of runs and locally filter the most recent
           * ones used by the dashboard.
           */
          const response = await fetchAllRuns(project.id, 1000, 0, 'functional');

          const recentRuns = response.data.filter((run: RunSummary) => {
            const runDate = new Date(run.created_at);
            return runDate >= thirtyDaysAgo;
          });

          runsAccumulator[project.id] = recentRuns;
        }

        setRunsByProject(runsAccumulator);
      } catch (err) {
        if (err instanceof AuthError) {
          return;
        }

        console.error('Failed to load runs:', err);
        setError('Failed to load recent run data. Please refresh the page.');
      }
    };

    loadRuns();
  }, [projects]);

  /**
   * Dashboard top metrics are strictly scoped to the currently selected
   * project. When no project is selected, they intentionally resolve to an
   * empty set rather than aggregating across projects.
   */
  const relevantRuns = useMemo(() => {
    if (currentProject && runsByProject[currentProject.id]) {
      return runsByProject[currentProject.id];
    }

    return [];
  }, [runsByProject, currentProject]);

  /**
   * Functional-only runs used for dashboard KPI cards. Generated functional
   * seed history is included so sample projects are explorable immediately.
   */
  const functionalRecentRuns = useMemo(
    () =>
      relevantRuns.filter(
        (run) =>
          run.run_type === 'functional' &&
          run.total_experiments > 0
      ),
    [relevantRuns]
  );

  /**
   * Count projects that already have at least one recent Quantum run.
   */
  const projectsUnderQuantum = useMemo(
    () => projects.filter((project) => (runsByProject[project.id] || []).length > 0).length,
    [projects, runsByProject]
  );

  const runsLast30d = functionalRecentRuns.length;

  /**
   * Identify the functional run with the best test reduction outcome.
   */
  const bestRun = useMemo(() => {
    if (functionalRecentRuns.length === 0) {
      return null;
    }

    return functionalRecentRuns.reduce((best, current) => {
      const currentReduction =
        current.reduction_percentage ??
        (1 - current.selected_experiments / current.total_experiments) * 100;
      const bestReduction = best
        ? (best.reduction_percentage ??
            (1 - best.selected_experiments / best.total_experiments) * 100)
        : 0;

      return currentReduction > bestReduction ? current : best;
    }, null as RunSummary | null);
  }, [functionalRecentRuns]);

  /**
   * Compute the percentage reduction achieved by the best run.
   */
  const bestReduction = useMemo(() => {
    if (!bestRun) {
      return null;
    }

    return (
      bestRun.reduction_percentage ??
      (1 - bestRun.selected_experiments / bestRun.total_experiments) * 100
    );
  }, [bestRun]);

  const bestRiskCoverageValue = useMemo(() => {
    if (!bestRun) {
      return null;
    }

    const coverage = bestRun.high_risk_coverage;
    return coverage === undefined || coverage === null ? null : Number(coverage);
  }, [bestRun]);

  const latestRun = useMemo(() => {
    if (functionalRecentRuns.length === 0) {
      return null;
    }

    return [...functionalRecentRuns].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
  }, [functionalRecentRuns]);

  const lastRunLabel = latestRun
    ? new Date(latestRun.created_at).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'No recent runs';

  const recommendedAction = useMemo(() => {
    if (!currentProject) {
      return 'Select a project to focus the control room on recent selection performance.';
    }

    if (!latestRun) {
      return 'Run your first selection to establish a risk baseline.';
    }

    const latestReduction =
      latestRun.reduction_percentage ??
      (latestRun.total_experiments > 0
        ? (1 - latestRun.selected_experiments / latestRun.total_experiments) * 100
        : null);
    const latestCoverage = latestRun.high_risk_coverage;

    if (latestReduction !== null && latestCoverage !== undefined && latestCoverage !== null) {
      return `Review the latest selection results. Your last run achieved ${formatPercentage(
        latestReduction
      )} test reduction with ${formatPercentage(Number(latestCoverage))} high-risk coverage.`;
    }

    return 'Run a new selection to refresh risk coverage and optimization signals.';
  }, [currentProject, latestRun]);

  const handleOpenProject = (project: ProjectSummary) => {
    if (currentProject?.id !== project.id) {
      setCurrentProject(project);
    }
    navigate('/results');
  };

  const handleRunSelection = (project: ProjectSummary) => {
    if (currentProject?.id !== project.id) {
      setCurrentProject(project);
    }
    navigate('/quantum');
  };

  return (
    <Box className="page-transition">
      <Box
        sx={{
          mb: 3.5,
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 4,
          background:
            'linear-gradient(135deg, rgba(15, 23, 32, 0.98) 0%, rgba(7, 10, 16, 0.98) 100%)',
          border: '1px solid rgba(57, 255, 20, 0.16)',
          boxShadow:
            '0 24px 70px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255,255,255,0.04)',
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack spacing={1.2}>
              <Chip
                size="small"
                icon={<CheckCircleIcon />}
                label="System Ready"
                sx={{
                  alignSelf: 'flex-start',
                  color: '#DFFFE6',
                  bgcolor: 'rgba(57,255,20,0.1)',
                  border: '1px solid rgba(57,255,20,0.2)',
                  '& .MuiChip-icon': { color: '#39FF14' },
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 850,
                  color: '#fff',
                  lineHeight: 1.05,
                  fontSize: { xs: '2rem', md: '2.75rem' },
                }}
              >
                Operational Risk Control Room
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(230,237,243,0.72)',
                  maxWidth: 720,
                  lineHeight: 1.7,
                }}
              >
                Monitor selection efficiency, risk coverage and execution impact across your projects.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.25,
              }}
            >
              <HeroSignal label="Last Run" value={lastRunLabel} />
              <HeroSignal label="Runs Last 30 Days" value={runsLast30d} />
              <HeroSignal
                label="Best Risk Coverage"
                value={
                  bestRiskCoverageValue === null
                    ? 'Unavailable'
                    : formatPercentage(bestRiskCoverageValue)
                }
              />
              <HeroSignal
                label="Best Test Reduction"
                value={bestReduction == null ? 'Unavailable' : formatPercentage(bestReduction)}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Box mb={3}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Box
        sx={{
          mb: 4,
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.035)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: '#39FF14',
                bgcolor: 'rgba(57,255,20,0.08)',
                border: '1px solid rgba(57,255,20,0.16)',
                flexShrink: 0,
              }}
            >
              <InsightsIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#E6EDF3' }}>
                Recommended Next Action
              </Typography>
              <Typography variant="body2" sx={{ color: '#9CA3AF', lineHeight: 1.65, mt: 0.4 }}>
                {recommendedAction}
              </Typography>
            </Box>
          </Stack>
          <ActionButton
            onClick={() => (latestRun ? navigate('/results') : navigate('/quantum'))}
            endIcon={<ArrowForwardIcon />}
            sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
          >
            {latestRun ? 'Open Results' : 'Run Selection'}
          </ActionButton>
        </Stack>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={750}>
            Project Portfolio
          </Typography>
          <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>
            {projectsUnderQuantum} {projectsUnderQuantum === 1 ? 'project' : 'projects'} monitored by Quantik Mind.
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        {projects.map((project) => {
          const runs = runsByProject[project.id] || [];
          const projectLatestRun = [...runs].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )[0];
          const isSelected = currentProject?.id === project.id;
          const projectType = projectLatestRun?.run_type === 'functional'
            ? 'Functional Testing'
            : projectLatestRun?.run_type
            ? projectLatestRun.run_type
            : 'Ready for Selection';
          const projectReduction =
            projectLatestRun && projectLatestRun.total_experiments > 0
              ? projectLatestRun.reduction_percentage ??
                (1 -
                  projectLatestRun.selected_experiments /
                    projectLatestRun.total_experiments) *
                  100
              : null;

          return (
            <Grid item xs={12} md={6} lg={4} key={project.id}>
              <Card
                className="enterprise-card hover-lift"
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  borderColor: isSelected ? 'rgba(57,255,20,0.24)' : 'rgba(255,255,255,0.08)',
                }}
              >
                <CardContent sx={{ p: 2.25 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25} mb={2}>
                    <Stack spacing={0.8} sx={{ minWidth: 0 }}>
                      <Typography fontWeight={760} sx={{ color: '#F8FAFC', lineHeight: 1.25 }}>
                        {project.name}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={0.8}>
                        <Chip size="small" label={projectType} />
                        <Chip size="small" label={`${runs.length} recent runs`} />
                      </Stack>
                    </Stack>
                    {isSelected && (
                      <Chip
                        size="small"
                        label="Active"
                        sx={{
                          color: '#DFFFE6',
                          bgcolor: 'rgba(57,255,20,0.1)',
                          border: '1px solid rgba(57,255,20,0.2)',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Stack>

                  {projectLatestRun ? (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#7D8590', textTransform: 'uppercase', fontWeight: 700 }}>
                        Latest Run
                      </Typography>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ width: '100%', mt: 0.75 }}
                      >
                        <Typography variant="body2" sx={{ color: '#E6EDF3', fontWeight: 700 }}>
                          {projectLatestRun.selected_experiments}/{projectLatestRun.total_experiments} tests
                        </Typography>

                        {projectReduction !== null ? (
                          <Typography color="#39ff14" fontWeight={760} variant="body2">
                            {formatPercentage(projectReduction)} reduction
                          </Typography>
                        ) : (
                          <Typography color="text.secondary" fontWeight={600} variant="body2">
                            reduction unavailable
                          </Typography>
                        )}
                      </Stack>

                      {projectLatestRun.total_experiments > 0 && (
                        <LinearProgress
                          variant="determinate"
                          value={
                            (projectLatestRun.selected_experiments /
                              projectLatestRun.total_experiments) *
                            100
                          }
                          sx={{
                            height: 7,
                            borderRadius: 999,
                            mt: 1.25,
                            bgcolor: 'rgba(255,255,255,0.08)',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 999,
                              background: 'linear-gradient(90deg, #39FF14 0%, #67D9FF 100%)',
                            },
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#9CA3AF', lineHeight: 1.55 }}>
                        No recent selections yet. Establish a baseline from the Selection workflow.
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ px: 2.25, pb: 2.25, pt: 0, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      width: '100%',
                      minWidth: 0,
                      '& > *': {
                        flex: '1 1 140px',
                        minWidth: 0,
                        width: 'auto !important',
                      },
                    }}
                  >
                    <ActionButton
                      fullWidth
                      onClick={() => handleOpenProject(project)}
                    >
                      Open Metrics
                    </ActionButton>
                    <ActionButton
                      fullWidth
                      onClick={() => handleRunSelection(project)}
                    >
                      Run Selection
                    </ActionButton>
                    {!isSelected && (
                      <ActionButton
                        fullWidth
                        onClick={() => setCurrentProject(project)}
                      >
                        Set Active
                      </ActionButton>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

/**
 * Render a summary metric card used in the dashboard overview grid.
 *
 * @param props - Metric configuration and content.
 * @returns A metric card element.
 */
const HeroSignal = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 2.5,
      bgcolor: 'rgba(255,255,255,0.035)',
      border: '1px solid rgba(255,255,255,0.08)',
      minHeight: 82,
    }}
  >
    <Typography variant="caption" sx={{ color: '#7D8590', textTransform: 'uppercase', fontWeight: 800 }}>
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ color: '#F8FAFC', fontWeight: 800, mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
);

export default HomePage;
